import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Modal,
  Alert,
  Typography,
  Tooltip,
  Empty,
  Descriptions,
  Row,
  Col,
  Divider
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  PlusOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import governanceService from '@services/admin/governanceService';
import { getInvoiceSettingsConfig } from '@config/governance/invoiceSettings';
import ListPageHeader from '@shared/ListPageHeader';
import { formatDateTime, formatDate } from '@utils/dateTime';

const { Text } = Typography;

const STATUS_COLORS = {
  draft: 'default',
  pending: 'processing',
  paid: 'success',
  partially_paid: 'warning',
  overdue: 'error',
  past_due: 'error',
  cancelled: 'default',
  refunded: 'purple',
  forgiven: 'cyan',
  merged: 'default'
};

const STATUS_ICONS = {
  draft: <FileTextOutlined />,
  pending: <ClockCircleOutlined />,
  paid: <CheckCircleOutlined />,
  partially_paid: <DollarOutlined />,
  overdue: <ExclamationCircleOutlined />,
  past_due: <ExclamationCircleOutlined />,
  cancelled: <FileTextOutlined />,
  refunded: <DollarOutlined />,
  forgiven: <CheckCircleOutlined />,
  merged: <FileTextOutlined />
};

const Invoices = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [invoiceConfig, setInvoiceConfig] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesRes, profileRes] = await Promise.all([
        governanceService.getInvoices(),
        governanceService.getBillingProfile()
      ]);

      if (invoicesRes.success) {
        setInvoices(invoicesRes.data.invoices || []);
      } else {
        message.error(invoicesRes.message || t('common.error.fetch_failed'));
      }

      // Load country-specific config from billing profile
      if (profileRes.success) {
        const countryCode = profileRes.data.billing_profile?.country_code;
        setInvoiceConfig(getInvoiceSettingsConfig(countryCode));
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetail = async (invoice) => {
    setDetailModalVisible(true);
    setDetailLoading(true);

    try {
      const response = await governanceService.getInvoice(invoice.id);
      if (response.success) {
        setSelectedInvoice(response.data.invoice);
      } else {
        message.error(response.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch invoice detail:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailModalVisible(false);
    setSelectedInvoice(null);
  };

  const handlePayInvoice = async (invoice) => {
    try {
      setPayLoading(true);
      const response = await governanceService.payInvoice(invoice.id);

      if (response.success) {
        message.success(t('admin.ai_center.invoices.payment_successful'));
        handleCloseDetail();
        fetchData();
      } else {
        message.error(response.message || t('admin.ai_center.invoices.payment_failed'));
      }
    } catch (error) {
      console.error('Failed to pay invoice:', error);
      message.error(t('admin.ai_center.invoices.payment_failed'));
    } finally {
      setPayLoading(false);
    }
  };

  const handleCancelInvoice = (invoice) => {
    Modal.confirm({
      title: t('admin.ai_center.invoices.cancel_confirm_title'),
      content: t('admin.ai_center.invoices.cancel_confirm_content', {
        invoice_number: invoice.invoice_number
      }),
      okText: t('admin.ai_center.invoices.cancel_confirm_ok'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        try {
          setCancelLoading(true);
          const response = await governanceService.cancelInvoice(invoice.id);

          if (response.success) {
            message.success(t('admin.ai_center.invoices.cancel_success'));
            handleCloseDetail();
            fetchData();
          } else {
            message.error(response.message || t('admin.ai_center.invoices.cancel_failed'));
          }
        } catch (error) {
          console.error('Failed to cancel invoice:', error);
          message.error(t('admin.ai_center.invoices.cancel_failed'));
        } finally {
          setCancelLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: t('admin.ai_center.invoices.invoice_number'),
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewDetail(record)} style={{ padding: 0 }}>
          {text}
        </Button>
      )
    },
    {
      title: t('admin.ai_center.invoices.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status]} icon={STATUS_ICONS[status]}>
          {t(`admin.ai_center.invoices.statuses.${status}`)}
        </Tag>
      )
    },
    {
      title: t('admin.ai_center.invoices.e_invoice_number'),
      key: 'e_invoice',
      render: (_, record) => {
        const eInv = record.e_invoice;
        if (!eInv) return '-';
        if (eInv.status === 'issued') {
          return (
            <Tooltip title={eInv.random_number ? `${t('admin.ai_center.invoices.e_invoice_random_number')}: ${eInv.random_number}` : undefined}>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {(invoiceConfig?.formatEInvoiceNumber || ((n) => n))(eInv.e_invoice_number)}
              </Tag>
            </Tooltip>
          );
        }
        if (eInv.status === 'pending') {
          return <Tag color="processing" icon={<ClockCircleOutlined />}>{t('admin.ai_center.invoices.e_invoice_pending')}</Tag>;
        }
        if (eInv.status === 'failed') {
          return <Tag color="error" icon={<ExclamationCircleOutlined />}>{t('admin.ai_center.invoices.e_invoice_failed')}</Tag>;
        }
        return '-';
      }
    },
    {
      title: t('admin.ai_center.invoices.period'),
      key: 'period',
      render: (_, record) => (
        <Text>
          {formatDate(record.billing_period_start)} ~ {formatDate(record.billing_period_end)}
        </Text>
      )
    },
    {
      title: t('admin.ai_center.invoices.total'),
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (total, record) => (
        <Text strong>
          {record.currency} {total?.toLocaleString()}
        </Text>
      )
    },
    {
      title: t('admin.ai_center.invoices.due_date'),
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date, record) => {
        if (!date) return '-';
        const isOverdue = record.overdue;
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {formatDate(date)}
            {isOverdue && (
              <Tag color="red" style={{ marginLeft: 8 }}>
                {t('admin.ai_center.invoices.overdue_days', { days: record.days_overdue })}
              </Tag>
            )}
          </Text>
        );
      }
    },
    {
      title: t('admin.ai_center.invoices.issued_at'),
      dataIndex: 'issued_at',
      key: 'issued_at',
      render: (date) => date ? formatDateTime(date) : '-'
    },
    {
      title: t('common.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleViewDetail(record)}
          >
            {t('common.button.view')}
          </Button>
          {(record.status === 'pending' || record.status === 'past_due') && (
            <Button
              type="primary"
              size="small"
              icon={<CreditCardOutlined />}
              loading={payLoading}
              onClick={() => handlePayInvoice(record)}
            >
              {t('admin.ai_center.invoices.pay_now_button')}
            </Button>
          )}
          {record.status === 'pending' && (
            <Button
              size="small"
              danger
              icon={<CloseCircleOutlined />}
              loading={cancelLoading}
              onClick={() => handleCancelInvoice(record)}
            >
              {t('admin.ai_center.invoices.cancel_button')}
            </Button>
          )}
        </Space>
      )
    }
  ];

  const pendingInvoice = invoices.find(inv => inv.status === 'pending');

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 24px 0' }}>
      <ListPageHeader items={['ai_center', 'ai_center.invoices']} basePath="/admin" />

      <div style={{ padding: '0 24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Pending Invoice Alert */}
          {pendingInvoice && (
            <Card>
              <Alert
                message={t('admin.ai_center.invoices.pending_credit_card_payment_alert')}
                description={
                  <div style={{ marginTop: 12 }}>
                    <Descriptions column={1} size="small" bordered>
                      <Descriptions.Item label={t('admin.ai_center.invoices.invoice_number')}>
                        <Text copyable={{ text: pendingInvoice.invoice_number }}>
                          {pendingInvoice.invoice_number}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('admin.ai_center.invoices.amount_due')}>
                        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                          {pendingInvoice.currency} {pendingInvoice.total?.toLocaleString()}
                        </Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('admin.ai_center.invoices.due_date')}>
                        <Text type={pendingInvoice.overdue ? 'danger' : undefined}>
                          {formatDate(pendingInvoice.due_date)}
                        </Text>
                      </Descriptions.Item>
                    </Descriptions>
                    <div style={{ marginTop: 16 }}>
                      {pendingInvoice.has_payment_method ? (
                        <Button
                          type="primary"
                          icon={<CreditCardOutlined />}
                          loading={payLoading}
                          onClick={() => handlePayInvoice(pendingInvoice)}
                        >
                          {t('admin.ai_center.invoices.pay_now_button')}
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={() => navigate('/ai_center/payment_methods')}
                        >
                          {t('admin.ai_center.invoices.add_payment_method')}
                        </Button>
                      )}
                    </div>
                  </div>
                }
                type="info"
                showIcon
                icon={<CreditCardOutlined />}
              />
            </Card>
          )}

          {/* Invoices List */}
          <Card
            title={
              <Space>
                <FileTextOutlined />
                {t('admin.ai_center.invoices.list_title')}
              </Space>
            }
          >
            {invoices.length > 0 ? (
              <Table
                dataSource={invoices}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => t('common.pagination.total', { total })
                }}
                size="small"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('admin.ai_center.invoices.no_invoices')}
              />
            )}
          </Card>
        </Space>
      </div>

      {/* Invoice Detail Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            {selectedInvoice?.invoice_number || t('admin.ai_center.invoices.detail_title')}
          </Space>
        }
        open={detailModalVisible}
        onCancel={handleCloseDetail}
        footer={[
          <Button key="close" onClick={handleCloseDetail}>
            {t('common.button.close')}
          </Button>,
          (selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'past_due') && (
            selectedInvoice?.has_payment_method ? (
              <Button
                key="pay"
                type="primary"
                icon={<CreditCardOutlined />}
                loading={payLoading}
                onClick={() => handlePayInvoice(selectedInvoice)}
              >
                {t('admin.ai_center.invoices.pay_now_button')}
              </Button>
            ) : (
              <Button
                key="add_payment"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  handleCloseDetail();
                  navigate('/ai_center/payment_methods');
                }}
              >
                {t('admin.ai_center.invoices.add_payment_method')}
              </Button>
            )
          ),
          selectedInvoice?.status === 'pending' && (
            <Button
              key="cancel"
              danger
              icon={<CloseCircleOutlined />}
              loading={cancelLoading}
              onClick={() => handleCancelInvoice(selectedInvoice)}
            >
              {t('admin.ai_center.invoices.cancel_button')}
            </Button>
          )
        ].filter(Boolean)}
        width={700}
      >
        {detailLoading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : selectedInvoice ? (
          <>
            {/* Invoice Status */}
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Tag color={STATUS_COLORS[selectedInvoice.status]} icon={STATUS_ICONS[selectedInvoice.status]} style={{ fontSize: '16px', padding: '4px 12px' }}>
                {t(`admin.ai_center.invoices.statuses.${selectedInvoice.status}`)}
              </Tag>
            </div>

            {/* Invoice Info */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label={t('admin.ai_center.invoices.invoice_number')}>
                <Text copyable>{selectedInvoice.invoice_number}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.invoices.invoice_type')}>
                {t(`admin.ai_center.invoices.types.${selectedInvoice.invoice_type}`)}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.invoices.period')} span={2}>
                {formatDate(selectedInvoice.billing_period_start)} ~ {formatDate(selectedInvoice.billing_period_end)}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.invoices.issued_at')}>
                {selectedInvoice.issued_at ? formatDateTime(selectedInvoice.issued_at) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('admin.ai_center.invoices.due_date')}>
                {selectedInvoice.due_date ? formatDate(selectedInvoice.due_date) : '-'}
              </Descriptions.Item>
              {selectedInvoice.paid_at && (
                <Descriptions.Item label={t('admin.ai_center.invoices.paid_at')} span={2}>
                  {formatDateTime(selectedInvoice.paid_at)}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* E-Invoice Info */}
            {selectedInvoice.e_invoice && (
              <>
                <Divider>{t('admin.ai_center.invoices.e_invoice_section')}</Divider>
                <Descriptions bordered column={2} size="small">
                  <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_status')}>
                    {selectedInvoice.e_invoice.status === 'issued' ? (
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        {t('admin.ai_center.invoices.e_invoice_issued')}
                      </Tag>
                    ) : selectedInvoice.e_invoice.status === 'pending' ? (
                      <Tag color="processing" icon={<ClockCircleOutlined />}>
                        {t('admin.ai_center.invoices.e_invoice_pending')}
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<ExclamationCircleOutlined />}>
                        {t('admin.ai_center.invoices.e_invoice_failed')}
                      </Tag>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_provider')}>
                    {selectedInvoice.e_invoice.provider === 'ecpay' ? 'ECPay' : selectedInvoice.e_invoice.provider}
                  </Descriptions.Item>
                  {selectedInvoice.e_invoice.e_invoice_number && (
                    <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_number')}>
                      <Text copyable strong>
                        {(invoiceConfig?.formatEInvoiceNumber || ((n) => n))(selectedInvoice.e_invoice.e_invoice_number)}
                      </Text>
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.random_number && (
                    <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_random_number')}>
                      <Text>{selectedInvoice.e_invoice.random_number}</Text>
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.carrier_type && (
                    <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_carrier')}>
                      {selectedInvoice.e_invoice.carrier_type}
                    </Descriptions.Item>
                  )}
                  {selectedInvoice.e_invoice.issued_at && (
                    <Descriptions.Item label={t('admin.ai_center.invoices.e_invoice_issued_at')}>
                      {formatDateTime(selectedInvoice.e_invoice.issued_at)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </>
            )}

            {/* Invoice Items */}
            {selectedInvoice.items && selectedInvoice.items.filter(item => item.item_type !== 'tax').length > 0 && (
              <>
                <Divider>{t('admin.ai_center.invoices.items')}</Divider>
                <Table
                  dataSource={selectedInvoice.items.filter(item => item.item_type !== 'tax')}
                  columns={(() => {
                    const hasUsageItems = selectedInvoice.items.some(item => item.item_type === 'usage');

                    const baseColumns = [
                      {
                        title: t('admin.ai_center.invoices.item_description'),
                        dataIndex: 'description',
                        key: 'description'
                      }
                    ];

                    if (hasUsageItems) {
                      baseColumns.push(
                        {
                          title: t('admin.ai_center.invoices.item_quantity'),
                          dataIndex: 'quantity',
                          key: 'quantity',
                          align: 'right',
                          render: (val, record) => record.item_type === 'usage' ? val : '-'
                        },
                        {
                          title: t('admin.ai_center.invoices.item_unit_price'),
                          dataIndex: 'unit_price',
                          key: 'unit_price',
                          align: 'right',
                          render: (val, record) => record.item_type === 'usage'
                            ? `${selectedInvoice.currency} ${val?.toLocaleString()}`
                            : '-'
                        }
                      );
                    }

                    baseColumns.push({
                      title: t('admin.ai_center.invoices.item_amount'),
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right',
                      render: (val) => (
                        <Text type={val < 0 ? 'success' : undefined}>
                          {selectedInvoice.currency} {val?.toLocaleString()}
                        </Text>
                      )
                    });

                    return baseColumns;
                  })()}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </>
            )}

            {/* Totals */}
            <Divider />
            <Row justify="end">
              <Col span={10}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={t('admin.ai_center.invoices.subtotal')}>
                    <Text>{selectedInvoice.currency} {selectedInvoice.subtotal?.toLocaleString()}</Text>
                  </Descriptions.Item>
                  {selectedInvoice.tax_amount > 0 && (
                    <Descriptions.Item label={t('admin.ai_center.invoices.tax')}>
                      <Text>{selectedInvoice.currency} {selectedInvoice.tax_amount?.toLocaleString()}</Text>
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label={t('admin.ai_center.invoices.total')}>
                    <Text strong style={{ fontSize: '18px' }}>
                      {selectedInvoice.currency} {selectedInvoice.total?.toLocaleString()}
                    </Text>
                  </Descriptions.Item>
                  {selectedInvoice.paid_amount > 0 && (
                    <>
                      <Descriptions.Item label={t('admin.ai_center.invoices.paid_amount')}>
                        <Text type="success">{selectedInvoice.currency} {selectedInvoice.paid_amount?.toLocaleString()}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label={t('admin.ai_center.invoices.outstanding')}>
                        <Text type="danger">{selectedInvoice.currency} {selectedInvoice.outstanding_amount?.toLocaleString()}</Text>
                      </Descriptions.Item>
                    </>
                  )}
                </Descriptions>
              </Col>
            </Row>
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default Invoices;
